/**
 * provenanceBuilder — AEGIS-8 provenance chain.
 *
 * Builds an immutable provenance record attached to every governedAegisCall
 * response. Provenance makes AEGIS reasoning end-to-end traceable:
 *
 *   - `promptHash`            SHA-256 of the prompt+context envelope so we can
 *                              prove which input produced this decision without
 *                              persisting the prompt verbatim.
 *   - `retrievalSources`      array of references the call consulted (Firestore
 *                              doc paths, KBRAIN corpus refs, URLs, file paths).
 *   - `reasoningTraceRef`     pointer back to the response's reasoningTrace
 *                              (either inline or { decisionIdRef }).
 *   - `confidenceCalibration` raw vs calibrated confidence + calibration version.
 *   - `timestampIso`          call completion time (ISO-8601 UTC).
 *   - `wrapperVersion`        version tag for the wrapper code path that
 *                              produced this provenance.
 *   - `callerChain`           array of upstream agentIds — populated when the
 *                              caller passes `_callerChain` in params (for
 *                              multi-hop governed delegation).
 *
 * ARGUS queries `aegis_decisions/{decisionId}.provenance` to reconstruct the
 * reasoning chain. The CF responsible for persisting `aegis_decisions` already
 * spreads the response into the document, so attaching provenance at the
 * wrapper return is the only change needed for end-to-end queryability.
 */

import hashPrompt from './promptHasher.js';
// INTEL-AEGIS-CA-1: freshness-aware multi-source merger
import { mergeProvenance, CONFLICT_POLICY } from './multiSourceMerger.js';

export const WRAPPER_VERSION = 'aegis-wrapper-1.2-ca1-multi-source';
export { CONFLICT_POLICY };

const CALIBRATION_VERSION = 'cog-meta-1.0';

function _normalizeRetrievalSources(params, result) {
  // INTEL-AEGIS-CA-1: if caller passes `_multiSources` (array of {docPath, timestampMs, ...}),
  // run freshness-weighted merge and return authoritative paths + conflict metadata.
  if (Array.isArray(params?._multiSources) && params._multiSources.length > 0) {
    const ctx = params?.context || {};
    const window = ctx.freshnessWindowMs || 0;
    const policy = params._sourceConflictPolicy || CONFLICT_POLICY.FRESHEST_WINS;
    const merged = mergeProvenance(params._multiSources, window, {
      policy,
      priorityOrder: params._sourcePriority,
    });
    // Return { authoritative, advisory, conflictStatus, conflictReason, sourceScores }
    // so _buildMultiSourceProvenance can attach it to the record.
    return { _multi: merged };
  }

  // Legacy: callers pass `_retrievalSources` as flat string array.
  const explicit = Array.isArray(params?._retrievalSources)
    ? params._retrievalSources.filter(s => typeof s === 'string' && s.length > 0)
    : [];
  if (explicit.length > 0) return explicit;

  const inferred = [];
  const ctx = params?.context || {};
  if (typeof ctx.bayesianPriorRef === 'string' && ctx.bayesianPriorRef) {
    inferred.push(ctx.bayesianPriorRef);
  }
  if (Array.isArray(ctx.sourceTimestamps)) {
    for (const ts of ctx.sourceTimestamps) {
      if (typeof ts === 'object' && ts && typeof ts.docPath === 'string') {
        inferred.push(ts.docPath);
      }
    }
  }
  if (typeof result?.decisionId === 'string') {
    // Always include the canonical aegis_decisions path so ARGUS can chain.
    inferred.push(`aegis_decisions/${result.decisionId}`);
  }
  return inferred;
}

function _normalizeReasoningTraceRef(result) {
  const trace = result?.reasoningTrace;
  if (trace === null || trace === undefined) {
    return result?.decisionId ? { decisionIdRef: result.decisionId } : null;
  }
  // Already a pointer object — pass through.
  if (typeof trace === 'object' && !Array.isArray(trace) && typeof trace.decisionIdRef === 'string') {
    return { decisionIdRef: trace.decisionIdRef };
  }
  // Inline array — reference by decisionId rather than duplicating the array
  // in two places on the same response object.
  if (Array.isArray(trace)) {
    return {
      inlineLength: trace.length,
      decisionIdRef: result?.decisionId ?? null,
    };
  }
  return null;
}

function _normalizeConfidenceCalibration(params, result) {
  const raw = params?._rawConfidence ?? null;
  const calibrated = (typeof result?.confidence === 'number') ? result.confidence : null;
  return {
    rawConfidence: typeof raw === 'number' ? raw : null,
    calibratedConfidence: calibrated,
    calibrationVersion: CALIBRATION_VERSION,
    metaD: (typeof result?.metaD === 'number') ? result.metaD : null,
  };
}

function _normalizeCallerChain(params) {
  const chain = Array.isArray(params?._callerChain) ? params._callerChain : [];
  const cleaned = chain.filter(c => typeof c === 'string' && c.length > 0);
  // Always append the current agentId so the chain is self-describing.
  if (typeof params?.agentId === 'string' && params.agentId) {
    cleaned.push(params.agentId);
  }
  return cleaned;
}

/**
 * Build a provenance record for an AEGIS response.
 *
 * @param {object} params - the original call params
 * @param {object} result - the response object (must already have decisionId
 *                          and reasoningTrace per AEGIS-7 presentation contract)
 * @returns {Promise<object>} provenance record
 */
export async function buildProvenance(params, result) {
  const promptInput = params?.prompt ?? params?.context ?? null;
  const promptHash = await hashPrompt(promptInput);

  const sourcesResult = _normalizeRetrievalSources(params, result);

  // INTEL-AEGIS-CA-1: multi-source provenance path
  if (sourcesResult && typeof sourcesResult === 'object' && sourcesResult._multi) {
    const { authoritative, advisory, conflictPolicy, conflictStatus, conflictReason, sourceScores } = sourcesResult._multi;
    return {
      promptHash,
      retrievalSources: authoritative.map(s => s.docPath ?? s),
      retrievalSourcesDetail: authoritative,
      advisorySources: advisory,
      sourceConflictResolution: { conflictPolicy, conflictStatus, conflictReason, sourceScores },
      reasoningTraceRef: _normalizeReasoningTraceRef(result),
      confidenceCalibration: _normalizeConfidenceCalibration(params, result),
      timestampIso: new Date().toISOString(),
      wrapperVersion: WRAPPER_VERSION,
      callerChain: _normalizeCallerChain(params),
    };
  }

  return {
    promptHash,
    retrievalSources: sourcesResult,
    reasoningTraceRef: _normalizeReasoningTraceRef(result),
    confidenceCalibration: _normalizeConfidenceCalibration(params, result),
    timestampIso: new Date().toISOString(),
    wrapperVersion: WRAPPER_VERSION,
    callerChain: _normalizeCallerChain(params),
  };
}

/**
 * Validate a provenance record's shape. Used by tests + ARGUS reader to assert
 * downstream consumers receive what AEGIS-8 promises.
 *
 * @param {object} prov
 * @returns {{ passed: boolean, violations: string[] }}
 */
export function validateProvenanceShape(prov) {
  const violations = [];
  if (prov === null || typeof prov !== 'object' || Array.isArray(prov)) {
    return { passed: false, violations: ['provenance must be an object'] };
  }
  if (typeof prov.promptHash !== 'string' || !prov.promptHash.startsWith('sha256:')) {
    violations.push('provenance.promptHash must be a string prefixed with "sha256:"');
  }
  if (!Array.isArray(prov.retrievalSources)) {
    violations.push('provenance.retrievalSources must be an array');
  }
  if (prov.reasoningTraceRef !== null && typeof prov.reasoningTraceRef !== 'object') {
    violations.push('provenance.reasoningTraceRef must be an object or null');
  }
  if (!prov.confidenceCalibration || typeof prov.confidenceCalibration !== 'object') {
    violations.push('provenance.confidenceCalibration must be an object');
  } else {
    const cc = prov.confidenceCalibration;
    if (cc.rawConfidence !== null && typeof cc.rawConfidence !== 'number') {
      violations.push('provenance.confidenceCalibration.rawConfidence must be a number or null');
    }
    if (cc.calibratedConfidence !== null && typeof cc.calibratedConfidence !== 'number') {
      violations.push('provenance.confidenceCalibration.calibratedConfidence must be a number or null');
    }
    if (typeof cc.calibrationVersion !== 'string' || cc.calibrationVersion === '') {
      violations.push('provenance.confidenceCalibration.calibrationVersion must be a non-empty string');
    }
  }
  if (typeof prov.timestampIso !== 'string' || Number.isNaN(Date.parse(prov.timestampIso))) {
    violations.push('provenance.timestampIso must be an ISO-8601 timestamp string');
  }
  if (typeof prov.wrapperVersion !== 'string' || prov.wrapperVersion === '') {
    violations.push('provenance.wrapperVersion must be a non-empty string');
  }
  if (!Array.isArray(prov.callerChain)) {
    violations.push('provenance.callerChain must be an array');
  }
  return { passed: violations.length === 0, violations };
}

export const PROVENANCE_FIELDS = Object.freeze([
  'promptHash',
  'retrievalSources',
  'reasoningTraceRef',
  'confidenceCalibration',
  'timestampIso',
  'wrapperVersion',
  'callerChain',
]);

export default buildProvenance;
